import { UserAddOutlined } from '@ant-design/icons';
import { getDeviceCount } from '@portal/actions/device';
import { deleteUser, myUsers } from '@portal/actions/user';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedTooltip from '@portal/components/AdvancedTooltip/AdvancedTooltip';
import UserCard from '@portal/components/UserCard/UserCard';

import Edit from '@portal/assets/svg/multiaccess-icons/ic_edit.svg?react';
import Delete from '@portal/assets/svg/onboarding-icons/ic_delete.svg?react';
import { getRouteStackPath } from '@portal/config/routes';
import useReduxState from '@portal/hooks/useReduxState';

import { translate } from '@portal/services/i18n';
import { Modal, Tabs, Typography } from 'antd';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { multiUser, myAccount } from '@portal/config/app.json';
import RolesType from '@portal/enum/rolesType';
import { UserRole } from '@portal/enum/userRole';

import { useTheme } from '@material-ui/core';
import { UserImpersonationActions } from '@portal/actions';
import AccessDeniedModal from '@portal/components/AccessDeniedModal/AccessDeniedModal';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import EmptyStateSection from '@portal/components/EmptyStateSection/EmptyStateSection';
import { UserImpersonationStatus } from '@portal/enum/userImpersonationStatus';
import { hasAccess } from '@portal/utils/hasAccess';

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';
import './MultiAccess.less';

const { Text } = Typography;
const { TabPane } = Tabs;

interface PendingRequest {
  id: string;
  targetUserId: string;
  targetUser: {
    id: string;
    name: string;
    login: string;
    taxIdentifier: string;
  };
  status: UserImpersonationStatus;
  impersonationId: string;
  createdAt: string;
}

enum TabKeys {
  USERS = 'users',
  PENDING_REQUESTS = 'pending_requests',
  SENT_REQUESTS = 'sent_requests',
}

const MultiAccessScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    user: { myUsersList, deleteUserStatus, roles },
    userImpersonation: userImpersonationData,
    device: { deviceCount },
    loading,
  } = useReduxState();

  const { palette } = useTheme();

  const [userId, setUserId] = useState('');
  const [userInfo, setUserInfo] = useState<models.MyUsers | null>(null);
  const [visible, setVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hasAccessModal, setHasAccessModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKeys>(TabKeys.USERS);
  const [confirmRequestModal, setConfirmRequestModal] = useState<{
    visible: boolean;
    type: 'accept' | 'reject' | null;
    request: PendingRequest | null;
  }>({
    visible: false,
    type: null,
    request: null,
  });
  const [cancelSentRequestModal, setCancelSentRequestModal] = useState<{
    visible: boolean;
    request: models.MyUsers | null;
  }>({
    visible: false,
    request: null,
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (
      hasAccess(roles, [
        RolesType.ACCOUNT_READ,
        RolesType.ACCOUNT_ADD_USER,
        RolesType.MANAGE_ACCOUNTS,
      ])
    ) {
      dispatch(myUsers());
    }
  }, [dispatch, roles]);

  useEffect(() => {
    if (deleteUserStatus && hasAccess(roles, RolesType.ACCOUNT_READ)) {
      setVisible(false);
      setUserId('');
      setUserInfo(null);

      dispatch(myUsers());
      dispatch(UserImpersonationActions.getUserImpersonationList());
    }
  }, [deleteUserStatus, dispatch, roles]);

  useEffect(() => {
    dispatch(UserImpersonationActions.getUserImpersonationList());
    dispatch(getDeviceCount());
  }, [dispatch]);

  const getPendingRequests = (): PendingRequest[] => {
    if (!userImpersonationData?.received) return [];

    return userImpersonationData.received
      .filter((item) => item.status === UserImpersonationStatus.PENDING)
      .map((item) => ({
        id: item.targetUserId,
        targetUserId: item.targetUserId,
        targetUser: item.targetUser,
        status: item.status as UserImpersonationStatus,
        impersonationId: item.id,
        createdAt: item.createdAt,
      }));
  };

  const getSentRequests = (): models.MyUsers[] => {
    if (!Array.isArray(myUsersList)) return [];

    return myUsersList.filter((user) => user.status === 'pending');
  };

  const getAcceptedUsers = (): models.MyUsers[] => {
    if (!Array.isArray(myUsersList)) return [];

    return myUsersList.filter((user) => user.status !== 'pending');
  };

  const handleAcceptRequest = async () => {
    if (confirmRequestModal.request?.impersonationId) {
      try {
        await dispatch(
          UserImpersonationActions.acceptUserImpersonation(
            confirmRequestModal.request.impersonationId
          )
        );
        setConfirmRequestModal({ visible: false, type: null, request: null });

        dispatch(myUsers());
        dispatch(UserImpersonationActions.getUserImpersonationList());
      } catch (error) {
        // Error is handled by the action
      }
    }
  };

  const handleRejectRequest = async () => {
    if (confirmRequestModal.request?.impersonationId) {
      try {
        await dispatch(
          UserImpersonationActions.rejectUserImpersonation(
            confirmRequestModal.request.impersonationId
          )
        );
        setConfirmRequestModal({ visible: false, type: null, request: null });

        dispatch(myUsers());
        dispatch(UserImpersonationActions.getUserImpersonationList());
      } catch (error) {
        // Error is handled by the action
      }
    }
  };

  const showConfirmRequestModal = (
    type: 'accept' | 'reject',
    request: PendingRequest
  ) => {
    setConfirmRequestModal({ visible: true, type, request });
  };

  const isMainAccount = () => {
    return userInfo?.role === UserRole.MAIN;
  };

  const hasAvailableDeviceSlots = () => {
    return (
      deviceCount && deviceCount.maxDevices - deviceCount.actualDevices > 0
    );
  };

  const getDeviceSlotsTooltip = () => {
    return translate('PAGES.MY_DEVICES.NO_SLOTS_MULTI_ACCESS');
  };

  const filterList = (list) => {
    if (!search) return list;

    const query = search.toLowerCase();

    return list.filter(
      (item: { name: string; login: string; email: string }) =>
        item?.name?.toLowerCase().includes(query) ||
        item?.login?.toLowerCase().includes(query) ||
        item?.email?.toLowerCase().includes(query)
    );
  };

  const pendingRequests = getPendingRequests();
  const sentRequests = getSentRequests();
  const acceptedUsers = getAcceptedUsers();

  const filteredPendingRequests = filterList(pendingRequests);
  const filteredSentRequests = filterList(sentRequests);
  const filteredAcceptedUsers = filterList(acceptedUsers);

  const tabOptions = [
    {
      visible: true,
      label: `${translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.USERS')} (${
        acceptedUsers.length
      })`,
      value: TabKeys.USERS,
      children: (
        <>
          {acceptedUsers.length === 0 ? (
            <EmptyStateSection
              icon={<UserAddOutlined />}
              title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_USERS_TITLE')}
              description={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_USERS_DESCRIPTION'
              )}
            />
          ) : (
            <div className="multiaccess-page__cards-grid">
              {filteredAcceptedUsers?.map((item) => (
                <UserCard
                  key={item.id}
                  user={item}
                  type="accepted"
                  roles={roles}
                  clickable
                  onClick={() => {
                    if (hasAccess(roles, RolesType.MANAGE_ACCOUNTS)) {
                      setUserId(item.id ?? '');
                      setUserInfo(item as models.MyUsers);
                      setVisible(true);
                    } else {
                      setHasAccessModal(true);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </>
      ),
    },
    {
      visible: !multiUser.hideInviteAndAccountSelect,
      label: `${translate(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PENDING_REQUESTS'
      )} (${pendingRequests.length})`,
      value: TabKeys.PENDING_REQUESTS,
      children: (
        <>
          {pendingRequests.length === 0 ? (
            <EmptyStateSection
              icon={<UserAddOutlined />}
              title={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_PENDING_REQUESTS_TITLE'
              )}
              description={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_PENDING_REQUESTS_DESCRIPTION'
              )}
            />
          ) : (
            <div className="multiaccess-page__cards-grid">
              {filteredPendingRequests?.map((request, index) => (
                <UserCard
                  key={index}
                  user={{
                    id: request.targetUserId,
                    name: request.targetUser?.name,
                    login: request.targetUser?.login,
                    taxIdentifier: request.targetUser?.taxIdentifier,
                  }}
                  type="pending"
                  roles={roles}
                  onAccept={() => showConfirmRequestModal('accept', request)}
                  onReject={() => showConfirmRequestModal('reject', request)}
                />
              ))}
            </div>
          )}
        </>
      ),
    },
    {
      visible: !multiUser.hideInviteAndAccountSelect,
      label: `${translate(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.SENT_REQUESTS'
      )} (${sentRequests.length})`,
      value: TabKeys.SENT_REQUESTS,
      children: (
        <>
          {sentRequests.length === 0 ? (
            <EmptyStateSection
              icon={<UserAddOutlined />}
              title={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_SENT_REQUESTS_TITLE'
              )}
              description={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.NO_SENT_REQUESTS_DESCRIPTION'
              )}
            />
          ) : (
            <div className="multiaccess-page__cards-grid">
              {filteredSentRequests?.map((item, index) => (
                <UserCard
                  key={index}
                  user={item}
                  type="sent"
                  roles={roles}
                  onCancelInvite={() =>
                    setCancelSentRequestModal({ visible: true, request: item })
                  }
                />
              ))}
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
          },
        ]}
      />
      <div className="multiaccess-page">
        <AdvancedPanelContentHeader
          testId="multiaccess-header"
          title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE')}
          description={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.DESCRIPTION')}
          goBackAction={() =>
            navigate(getRouteStackPath('PROFILE', 'PROFILE_PAGE'))
          }
        />
        <AccessListControl
          accessRole={[
            RolesType.ACCOUNT_READ,
            RolesType.ACCOUNT_ADD_USER,
            RolesType.MANAGE_ACCOUNTS,
          ]}
        >
          <>
            <div className="multiaccess-page__buttons">
              {!multiUser.hideInviteAndAccountSelect && (
                <AdvancedTooltip
                  title={
                    !hasAvailableDeviceSlots()
                      ? getDeviceSlotsTooltip()
                      : undefined
                  }
                >
                  <AdvancedButton
                    startIcon={<UserAddOutlined />}
                    text={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER'
                    )}
                    onClick={() =>
                      navigate(getRouteStackPath('PROFILE', 'INVITE_USER'))
                    }
                    disabled={
                      !hasAccess(roles, [RolesType.ACCOUNT_ADD_USER]) ||
                      !hasAvailableDeviceSlots()
                    }
                  />
                </AdvancedTooltip>
              )}
              <AdvancedTooltip
                title={
                  !hasAvailableDeviceSlots()
                    ? getDeviceSlotsTooltip()
                    : undefined
                }
              >
                <AdvancedButton
                  size="large"
                  variant="outlined"
                  text={translate(
                    'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CREATE_USER'
                  )}
                  disabled={
                    !hasAccess(roles, [RolesType.ACCOUNT_ADD_USER]) ||
                    !hasAvailableDeviceSlots()
                  }
                  onClick={() =>
                    navigate(
                      getRouteStackPath('PROFILE', 'ACCOUNT_MULTI_ACCESS')
                    )
                  }
                />
              </AdvancedTooltip>
            </div>
            <AdvancedCard testId="multiaccess-card">
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as TabKeys)}
                type="card"
              >
                {tabOptions
                  .filter((tab) => tab.visible)
                  .map((tab) => (
                    <TabPane tab={tab.label} key={tab.value}>
                      <div className="multiaccess-page__input-search">
                        <AdvancedInput
                          label={translate(
                            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.SEARCH_USERS'
                          )}
                          variant="outlined"
                          value={search}
                          onChange={(value) => setSearch(value)}
                        />
                      </div>
                      {tab.children}
                    </TabPane>
                  ))}
              </Tabs>
            </AdvancedCard>
          </>
        </AccessListControl>
        <div className="multiaccess-page__button" style={{ marginTop: 32 }}>
          {!hasAccess(roles, [RolesType.MY_ACCOUNT]) && (
            <AdvancedButton
              size="large"
              fullWidth
              text={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.MY_PERMISSIONS')}
              onClick={() =>
                navigate(
                  getRouteStackPath('PROFILE', 'MY_PERMISSIONS_MULTI_ACCESS')
                )
              }
            />
          )}
        </div>
      </div>
      <Modal
        open={visible}
        footer={null}
        title={null}
        width={550}
        centered
        closable
        onCancel={() => setVisible(false)}
      >
        <>
          {isMainAccount() && (
            <div
              style={{
                padding: '16px',
                border: `1px solid ${palette.success.main}`,
                borderRadius: '6px',
                marginBottom: '16px',
              }}
            >
              <Text style={{ color: palette.success.main, fontSize: '14px' }}>
                {translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.MAIN_ACCOUNT_MESSAGE'
                )}
              </Text>
            </div>
          )}

          {hasAccess(roles, RolesType.MANAGE_ACCOUNTS) &&
          myAccount.enableEditMultiAccessUser &&
          userInfo?.role !== UserRole.EXTERNAL_MULTIACCESS &&
          !isMainAccount() ? (
            <div
              className="action-display"
              onClick={() =>
                navigate(
                  getRouteStackPath('PROFILE', 'EDIT_MULTI_ACCESS_USER'),
                  {
                    state: {
                      userId,
                      userInfo,
                    },
                  }
                )
              }
            >
              <div className="action-display__action">
                <div className="action-display__action__options">
                  <Edit width={20} height={20} className="mr-4" />
                  <div className="action-display__action__options__container">
                    <ThemedText
                      variant="body"
                      className="action-display__action__options__title-document"
                    >
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.EDIT_USER_INFO'
                      )}
                    </ThemedText>
                    <ThemedText
                      variant="body"
                      className="action-display__action__options__text"
                    >
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.DESCRIPTION_EDIT_USER_INFO'
                      )}
                    </ThemedText>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {hasAccess(roles, RolesType.MANAGE_ACCOUNTS) &&
            userInfo?.role === UserRole.EXTERNAL_MULTIACCESS &&
            !isMainAccount() && (
              <div
                className="action-display"
                onClick={() => {
                  if (userId) {
                    navigate(
                      getRouteStackPath(
                        'PROFILE',
                        'EDIT_EXTERNAL_USER'
                      ).replace(':id', userId)
                    );
                  }
                }}
              >
                <div className="action-display__action">
                  <div className="action-display__action__options">
                    <Edit width={20} height={20} className="mr-4" />
                    <div className="action-display__action__options__container">
                      <ThemedText
                        variant="body"
                        className="action-display__action__options__title-document"
                      >
                        {translate(
                          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.EDIT_EXTERNAL_USER_PERMISSIONS'
                        )}
                      </ThemedText>
                      <ThemedText
                        variant="body"
                        className="action-display__action__options__text"
                      >
                        {translate(
                          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.DESCRIPTION_EDIT_EXTERNAL_USER_PERMISSIONS'
                        )}
                      </ThemedText>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {hasAccess(roles, RolesType.MANAGE_ACCOUNTS) &&
            userInfo?.role !== UserRole.EXTERNAL_MULTIACCESS &&
            !isMainAccount() && (
              <div
                className="action-display"
                onClick={() =>
                  navigate(
                    getRouteStackPath('PROFILE', 'PERMISSIONS_MULTI_ACCESS'),
                    {
                      state: {
                        userId,
                      },
                    }
                  )
                }
              >
                <div className="action-display__action">
                  <div className="action-display__action__options">
                    <Edit width={20} height={20} className="mr-4" />
                    <div className="action-display__action__options__container">
                      <ThemedText
                        variant="body"
                        className="action-display__action__options__title-document"
                      >
                        {translate(
                          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CHANGE_PERMISSIONS'
                        )}
                      </ThemedText>
                      <ThemedText
                        variant="body"
                        className="action-display__action__options__text"
                      >
                        {translate(
                          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.DESCRIPTION_CHANGE_PERMISSIONS'
                        )}
                      </ThemedText>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {hasAccess(roles, RolesType.MANAGE_ACCOUNTS) && !isMainAccount() && (
            <div
              className="action-display"
              onClick={() => {
                setConfirmDelete(true);
                setVisible(false);
              }}
            >
              <div className="action-display__action">
                <div className="action-display__action__options">
                  <Delete width={20} height={20} className="mr-4" />
                  <div className="action-display__action__options__container">
                    <ThemedText
                      variant="body"
                      className="action-display__action__options__title-document"
                    >
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.DELETE_USER'
                      )}
                    </ThemedText>
                    <ThemedText
                      variant="body"
                      className="action-display__action__options__text"
                    >
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.DESCRIPTION_DELETE_USER'
                      )}
                    </ThemedText>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      </Modal>
      <Modal
        open={confirmDelete}
        title={translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CONFIRM_DELETE_USER'
        )}
        onOk={async () => {
          try {
            if (userInfo?.role === UserRole.EXTERNAL_MULTIACCESS) {
              await dispatch(
                UserImpersonationActions.cancelUserImpersonation(userId)
              );
            } else {
              await dispatch(deleteUser(userId));
            }
            setConfirmDelete(false);
            setUserId('');
            setUserInfo(null);

            dispatch(myUsers());
            dispatch(UserImpersonationActions.getUserImpersonationList());
          } catch (error) {
            // Error is handled by the action
          }
        }}
        onCancel={() => setConfirmDelete(false)}
        okText={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.DELETE')}
        cancelText={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CANCEL')}
        confirmLoading={loading.amount > 0}
      >
        <ThemedText variant="body">
          {translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CONFIRM_DESCRIPTION_DELETE_USER'
          )}
        </ThemedText>
      </Modal>
      <Modal
        title={
          confirmRequestModal.type === 'accept'
            ? translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.CONFIRM_ACCEPT_REQUEST_TITLE'
              )
            : translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.CONFIRM_REJECT_REQUEST_TITLE'
              )
        }
        open={confirmRequestModal.visible}
        onOk={
          confirmRequestModal.type === 'accept'
            ? handleAcceptRequest
            : handleRejectRequest
        }
        onCancel={() =>
          setConfirmRequestModal({ visible: false, type: null, request: null })
        }
        okText={
          confirmRequestModal.type === 'accept'
            ? translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.ACCEPT_REQUEST')
            : translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.REJECT_REQUEST')
        }
        cancelText={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CANCEL')}
        confirmLoading={loading.amount > 0}
      >
        <ThemedText variant="body">
          {confirmRequestModal.type === 'accept'
            ? translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CONFIRM_ACCEPT_REQUEST')
            : translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CONFIRM_REJECT_REQUEST')}
        </ThemedText>
        {confirmRequestModal.request ? (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: palette.grey[100],
              borderRadius: '4px',
            }}
          >
            <ThemedText variant="body">
              <strong>Usuário:</strong>{' '}
              {confirmRequestModal.request.targetUser.name}
            </ThemedText>
            <ThemedText variant="body">
              <strong>Login:</strong>{' '}
              {confirmRequestModal.request.targetUser.login}
            </ThemedText>
            <ThemedText variant="body">
              <strong>CPF/CNPJ:</strong>{' '}
              {confirmRequestModal.request.targetUser.taxIdentifier}
            </ThemedText>
          </div>
        ) : null}
      </Modal>
      <Modal
        open={cancelSentRequestModal.visible}
        title={translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CANCEL_INVITE_USER'
        )}
        onOk={async () => {
          try {
            await dispatch(
              UserImpersonationActions.cancelUserImpersonation(
                cancelSentRequestModal.request?.id || ''
              )
            );
            setCancelSentRequestModal({ visible: false, request: null });
            setUserId('');
            setUserInfo(null);
            dispatch(myUsers());
            setActiveTab(TabKeys.USERS);
            dispatch(UserImpersonationActions.getUserImpersonationList());
          } catch (error) {
            // Error is handled by the action
          }
        }}
        onCancel={() =>
          setCancelSentRequestModal({ visible: false, request: null })
        }
        okText={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CONFIRM')}
        cancelText={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CANCEL')}
        confirmLoading={loading.amount > 0}
      >
        <ThemedText variant="body">
          {translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.MULTI_ACCESS.CONFIRM_DESCRIPTION_DELETE_USER'
          )}
        </ThemedText>
      </Modal>
      <AccessDeniedModal
        visible={hasAccessModal}
        setVisible={setHasAccessModal}
      />
    </>
  );
};

export default MultiAccessScreen;
